'use client';

import dynamic from 'next/dynamic';
import React, { useEffect, useState } from 'react';
import { When } from 'react-if';

import { Box } from '@nerdwallet/currency/components/Box';
import ModalManager from '@components/modal-manager';

import { isDev, isStaging } from '@constants/client';

import { useAppSelector, useAppDispatch } from '@lib/hooks/redux-hooks';
import CurrentDateContext from '@lib/contexts/current-date-context';
import { getIsMobile } from '@lib/redux/selectors/browser';
import { useDisplayEphemeralAlert } from '@lib/hooks/nerdwallet-plus';
import { windowResized } from '@lib/redux/actions/browser';

import { throttle } from '@lib/utils';

import styles from './layout.module.scss';

const DashboardDevTools = dynamic(
  () => import('@components/dashboard-dev-tools'),
);

const HomeLayout = ({
  children,
}: Readonly<{
  children: React.ReactNode;
}>) => {
  const [appDateOverride, setAppDateOverride] = useState(new Date());
  const dispatch = useAppDispatch();

  useEffect(() => {
    dispatch(windowResized({}));

    const dispatchWindowResizedAction = () => {
      dispatch(windowResized({}));
    };

    const throttledOnResize = throttle(dispatchWindowResizedAction, 200);
    // set up throttled function on resize actions
    window.addEventListener('resize', () => {
      throttledOnResize();
    });

    return () => {
      window.removeEventListener('resize', throttledOnResize);
    };
  }, [dispatch]);

  const isMobile = useAppSelector(getIsMobile);
  useDisplayEphemeralAlert({ isMobile });

  return (
    <Box
      className={styles.container}
      display="flex"
      flexDirection="column"
      justifyContent="space-between"
    >
      <CurrentDateContext.Provider
        value={{
          currentDate: appDateOverride,
          updateDate: setAppDateOverride,
        }}
      >
        <When condition={isDev || isStaging}>
          <DashboardDevTools />
        </When>
        <div>{children}</div>
        <ModalManager />
      </CurrentDateContext.Provider>
    </Box>
  );
};

export default HomeLayout;
